<template>
  <div class="transaction-layout-compact-block">
    <div class="transaction-layout-compact">
      <AccountIcon :icon="sourceAccount.icon || ''"
                   :class="{'opacity-50':transaction.status === 0}"
                   :icon-color="sourceAccount.icon_color"
                   :icon-bg="sourceAccount.icon_bg"/>
      <div class="transaction-layout-compact__body">
        <div class="transaction-layout-compact__line">
          <AccountName :name="sourceAccount.title"
                       :class="{'opacity-50':transaction.status === 0}"
                       class="min-w-0 flex-1"/>
          <div class="transaction-layout-compact__amount">
            <TransferReceivedHint v-if="transferSumsDiffer"
                                  :sum="pairedTransfer.income.sum"
                                  :currency="destinationAccount.currency"/>
            <Value :sum="shownSum"
                   :sum-base="shownSumBase"
                   :currency="sourceAccount.currency"
                   :currency-display="currencyDisplay"
                   :isExpense="shownIsExpense"
                   :class="{'opacity-50':transaction.status === 0}"
                   :type="type"/>
            <div v-if="displayActions" class="transaction-layout-compact__actions">
              <Actions @remove="$emit('remove')"
                       @edit="$emit('edit')"/>
            </div>
          </div>
        </div>
        <div v-if="showMeta" class="transaction-layout-compact__meta">
          <div v-if="destinationAccount"
               class="min-w-0 truncate text-2sm text-text-secondary mr-4"
               :class="{'opacity-50':transaction.status === 0}">
            {{ destinationAccount.title }}
          </div>
          <Category v-else-if="transactionCategory"
                    :category="transactionCategory"
                    :class="{'opacity-50':transaction.status === 0}"/>
          <Comment v-if="transaction.comment || (!!transaction.transfer_id && !destinationAccount)"
                   :transaction="transaction"
                   :suppress-transfer="!!destinationAccount"
                   :comment="transaction.comment"
                   :class="{'opacity-50':transaction.status === 0}"
                   class="min-w-0"/>
        </div>
        <div v-if="showExtra" class="transaction-layout-compact__extra">
          <Contractor v-if="!!transaction.contractor && displayContractor"
                      :contractor="transaction.contractor"
                      :class="{'opacity-50':transaction.status === 0}"/>
          <Tags v-if="!!transaction.tags.length"
                :tags="transaction.tags"
                :type="type"
                class="flex-wrap min-w-0 flex-1"
                :class="{'opacity-50':transaction.status === 0}"/>
          <StatusPending v-if="transaction.status === 0"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import transactionCurrencyDisplayProp from "@/mixins/transactionCurrencyDisplayProp";
import AccountIcon from "@/components/common/Transaction/TransactionItem/AccountIcon";
import AccountName from "@/components/common/Transaction/TransactionItem/AccountName";
import Category from "@/components/common/Transaction/TransactionItem/Category";
import Comment from "@/components/common/Transaction/TransactionItem/Comment";
import Contractor from "@/components/common/Transaction/TransactionItem/Contractor";
import Tags from "@/components/common/Transaction/TransactionItem/Tags";
import Value from "@/components/common/Transaction/TransactionItem/Value";
import Actions from "@/components/common/Transaction/TransactionItem/Actions";
import StatusPending from "@/components/common/Transaction/TransactionItem/StatusPending";
import TransferReceivedHint from "@/components/common/Transaction/TransactionItem/TransferReceivedHint";

export default {
  name: "TransactionLayoutCompact",
  components: {TransferReceivedHint, StatusPending, AccountName, AccountIcon, Category, Comment, Contractor, Tags, Value, Actions},
  mixins:[transactionCurrencyDisplayProp],
  props: {
    transaction: {
      type: Object,
      required: true
    },
    displayContractor: {
      type: Boolean
    },
    markExpenseCategory: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('space', ['currentUserIsWatcher']),
    type() {
      if (!!this.transaction.transfer_id) {
        return 'transfer'
      } else {
        return this.transaction.type === 1 ? 'income' : 'expense'
      }
    },
    transactionCategory() {
      if (this.transaction.type === 1) {
        return this.transaction.income;
      } else {
        return this.transaction.expense;
      }
    },
    pairedTransfer() {
      if (!this.markExpenseCategory || !this.transaction.transfer_id) return null;
      const transfer = this.transaction.transfer;
      if (!transfer || !transfer.expense || !transfer.income) return null;
      if (!transfer.expense.account || !transfer.income.account) return null;
      return transfer;
    },
    sourceAccount() {
      return this.pairedTransfer ? this.pairedTransfer.expense.account : this.transaction.account;
    },
    destinationAccount() {
      return this.pairedTransfer ? this.pairedTransfer.income.account : null;
    },
    shownSum() {
      const sum = this.pairedTransfer ? this.pairedTransfer.expense.sum : this.transaction.sum;
      return Number(sum);
    },
    shownSumBase() {
      const sum = this.pairedTransfer ? this.pairedTransfer.expense.sum_base : this.transaction.sum_base;
      return sum == null ? sum : Number(sum);
    },
    shownIsExpense() {
      if (this.pairedTransfer) return true;
      return this.transaction.type === 2 || this.transaction.type === 4;
    },
    transferSumsDiffer() {
      if (!this.pairedTransfer) return false;
      const from = Number(this.pairedTransfer.expense.sum);
      const to = Number(this.pairedTransfer.income.sum);
      if (!Number.isFinite(from) || !Number.isFinite(to)) return false;
      return Math.round(from * 100) !== Math.round(to * 100);
    },
    showMeta() {
      return !!this.destinationAccount
        || !!this.transactionCategory
        || !!this.transaction.comment
        || (!!this.transaction.transfer_id && !this.destinationAccount);
    },
    showExtra() {
      return (!!this.transaction.contractor && this.displayContractor)
        || !!this.transaction.tags.length
        || this.transaction.status === 0;
    },
    displayActions() {
      return !this.currentUserIsWatcher && this.transaction.type !== 3 && this.transaction.type !== 4;
    }
  },
  i18n: {
    messages: {
      en: {
        transactionIsPending: 'Transaction is processing...'
      },
      ru: {
        transactionIsPending: 'Транзакция обрабатывается...'
      }
    }
  }
}
</script>

<style scoped lang="scss">
.transaction-layout-compact-block {
  @apply relative py-4 -mx-2 pl-2;
  margin-bottom: -1px;

  &:not(:last-of-type):after {
    @apply bg-border rounded w-full left-0 absolute rounded;
    content: '';
    height: 1px;
    bottom: 1px;
  }
}

.transaction-layout-compact {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: 0.9rem;
  align-items: start;

  > :first-child {
    margin-right: 0;
  }
}

.transaction-layout-compact__body {
  min-width: 0;
}

.transaction-layout-compact__line {
  display: flex;
  align-items: center;
  min-width: 0;
}

.transaction-layout-compact__amount {
  display: flex;
  align-items: center;
  flex: none;
  margin-left: 1.2rem;
  white-space: nowrap;
}

.transaction-layout-compact__actions {
  width: 0;
  margin-left: 0;
  opacity: 0;
  overflow: hidden;
  flex: none;
  transition: width .2s, margin .2s, opacity .2s;
}

.transaction-layout-compact-block:hover .transaction-layout-compact__actions {
  width: 15px;
  margin-left: 0.625rem;
  opacity: 1;
}

.transaction-layout-compact__meta {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: 0.3rem;

  > :first-child {
    flex: 0 0 auto;
    min-width: 0;
    max-width: 100%;
  }

  > :first-child:not(:last-child) {
    max-width: 70%;
  }

  > :last-child:not(:first-child) {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.transaction-layout-compact__extra {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  margin-top: 0.4rem;
  margin-left: -0.5rem;
}
</style>